// Access — who can see the books, who can change them, and the log of every
// change. Admins grant; nobody grants to themself; the database enforces it.
import { useCallback, useEffect, useState } from 'react';
import { useAuth, useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { errorText } from '../../lib/backbarApi';
import { dateTime } from '../../lib/format';
import { access } from '../../lib/ledger/api';
import type { AccessLevel, AccessRow, AuditRow } from '../../lib/ledger/types';

const NO_ACCOUNT = /no account with that email/i;

/** Plain words for an audit action; falls back to a readable version of the raw key. */
const ACTION_WORDS: Record<string, string> = {
  'books.access.set': 'Changed access',
  'books.invite': 'Invited someone',
  'books.close_month': 'Closed an Apple month',
  'books.pull_apple': 'Pulled from Apple',
  'books.pull_costs': 'Pulled from the bank',
  'books.record_payout': 'Recorded a payout',
  'books.send_payout': 'Sent a payout request',
  'books.refresh_payout': 'Checked Mercury',
  'books.settings_set': 'Changed Apple settings',
  'llc.close_period': 'Closed a ledger month',
  'llc.reopen_period': 'Reopened a ledger month',
  'llc.journal_entries.insert': 'Posted an entry',
  'llc.journal_entries.update': 'Changed an entry',
  'llc.journal_entries.delete': 'Deleted an entry',
  'llc.journal_lines.insert': 'Added entry lines',
  'llc.journal_lines.update': 'Changed entry lines',
  'llc.journal_lines.delete': 'Removed entry lines',
  'llc.periods.insert': 'Created a ledger month',
  'llc.periods.update': 'Changed a ledger month',
  'llc.accounts.insert': 'Added an account',
  'llc.accounts.update': 'Changed an account',
  'llc.accounts.delete': 'Removed an account',
  'llc.vendors.insert': 'Added a vendor',
  'llc.vendors.update': 'Changed a vendor',
  'llc.vendors.delete': 'Removed a vendor',
  'llc.bank_accounts.insert': 'Added a bank account',
  'llc.bank_accounts.update': 'Changed a bank account',
  'llc.bank_transactions.insert': 'Imported a bank transaction',
  'llc.bank_transactions.update': 'Changed a bank transaction',
  'llc.bank_transactions.delete': 'Removed a bank transaction',
  'llc.bank_imports.insert': 'Imported bank rows',
  'llc.bank_reconciliations.insert': 'Reconciled a bank account',
  'llc.fixed_assets.insert': 'Added an asset',
  'llc.fixed_assets.update': 'Changed an asset',
  'llc.fixed_assets.delete': 'Removed an asset',
  'llc.depreciation_runs.insert': 'Ran depreciation',
  'llc.settings.insert': 'Changed ledger settings',
  'llc.settings.update': 'Changed ledger settings',
};
const OP_WORDS: Record<string, string> = { insert: 'Added', update: 'Changed', delete: 'Removed' };

function actionWords(action: string): string {
  if (ACTION_WORDS[action]) return ACTION_WORDS[action];
  const m = /^(?:llc|books)\.([a-z_]+)\.(insert|update|delete)$/.exec(action);
  if (m) return `${OP_WORDS[m[2]]} ${m[1].replace(/_/g, ' ')}`;
  return action;
}

function roleWordsFor(r: AccessRow): string {
  if (r.is_admin) return 'Owner';
  return r.level === 'edit' ? 'Accountant' : 'Guest';
}

function levelWords(r: AccessRow): string {
  if (r.level === 'edit') return 'Edit';
  if (r.level === 'view') return 'View';
  return r.is_admin ? 'View (as owner)' : 'None';
}

function targetWords(a: AuditRow): string {
  const d = a.details ?? {};
  const email = d.email ?? d.target_email ?? d.target;
  if (typeof email === 'string' && email) return email;
  if (typeof d.month === 'string') return d.month;
  if (typeof d.memo === 'string' && d.memo) return d.memo;
  const id = a.target_id ? `${a.target_id.slice(0, 8)}…` : '';
  return [a.target_type, id].filter(Boolean).join(' ');
}

export function AccessTab() {
  const { isAdmin } = useBooksAccess();
  const { user, admin } = useAuth();
  const superAdmin = admin?.role === 'super_admin';

  const [rows, setRows] = useState<AccessRow[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [auditErr, setAuditErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [actErr, setActErr] = useState<unknown>(null);

  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<AccessLevel>('view');
  const [giving, setGiving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLevel, setInviteLevel] = useState<AccessLevel>('view');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    const [a, l] = await Promise.allSettled([access.list(), access.audit(200)]);
    if (a.status === 'fulfilled') { setRows(a.value ?? []); setErr(null); } else { setRows(null); setErr(a.reason); }
    if (l.status === 'fulfilled') { setAudit(l.value ?? []); setAuditErr(null); } else { setAudit(null); setAuditErr(l.reason); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act(label: string, fn: () => Promise<unknown>) {
    setActErr(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      await load();
    } catch (e) {
      setActErr(e);
    }
  }

  async function giveByEmail() {
    const target = email.trim().toLowerCase();
    if (!target) return;
    setGiving(true);
    setActErr(null);
    setDone(null);
    setShowInvite(false);
    try {
      await access.setByEmail(target, level);
      setDone(`${target} can now ${level} the books.`);
      setEmail('');
      await load();
    } catch (e) {
      setActErr(e);
      if (NO_ACCOUNT.test(errorText(e))) {
        setShowInvite(true);
        setInviteEmail(target);
        setInviteLevel(level);
      }
    } finally {
      setGiving(false);
    }
  }

  async function invite() {
    const target = inviteEmail.trim().toLowerCase();
    if (!target) return;
    setInviting(true);
    setActErr(null);
    setDone(null);
    try {
      const r = await access.invite(target, inviteLevel);
      setDone(`Invited ${r?.email ?? target}. They get an email with a sign-in link and land straight in Books with ${inviteLevel} access.`);
      setInviteEmail('');
      setShowInvite(false);
      await load();
    } catch (e) {
      setActErr(e);
    } finally {
      setInviting(false);
    }
  }

  return (
    <>
      <div className="lp-panel" style={{ marginBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.55 }}>
          View lets someone read every page of the books; Edit lets them post and change entries.
          Owners (admins) start with View and nothing more.
          Nobody can change their own access, so each owner asks the other.
          Every change to access, and every entry, is logged below.
        </p>
      </div>

      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actErr} />

      <Section title="Who has access">
        <ErrorNote error={err} notConfigured="The ledger is not set up yet." />
        {rows === null && !err && <Empty>Loading…</Empty>}
        {rows && rows.length === 0 && <Empty>Nobody is listed yet.</Empty>}
        {rows && rows.length > 0 && (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Access</th>
                  <th>Granted by</th>
                  <th>Granted</th>
                  <th>Last sign-in</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const me = user?.id === r.user_id;
                  return (
                    <tr key={r.user_id}>
                      <td>{r.email ?? <span className="muted">{r.user_id.slice(0, 8)}…</span>}{me && <> <span className="chip chip--info">you</span></>}</td>
                      <td>{roleWordsFor(r)}{r.admin_role ? <span className="muted"> · {r.admin_role.replace(/_/g, ' ')}</span> : null}</td>
                      <td>
                        <span className={r.level === 'edit' ? 'lp-tag lp-tag--good' : r.level === 'view' || r.is_admin ? 'lp-tag' : 'lp-tag lp-tag--warn'}>{levelWords(r)}</span>
                      </td>
                      <td>{r.granted_by_email ?? '—'}</td>
                      <td>{r.granted_at ? dateTime(r.granted_at) : '—'}</td>
                      <td>{r.last_sign_in_at ? dateTime(r.last_sign_in_at) : 'Never'}</td>
                      {isAdmin && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {me ? (
                            <>
                              <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" disabled>Set View</button>{' '}
                              <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" disabled>Set Edit</button>
                              <div className="lp-field-hint">You cannot change your own access.</div>
                            </>
                          ) : (
                            <>
                              <button type="button" className="lp-btn lp-btn--small" disabled={r.level === 'view'} onClick={() => act(`${r.email ?? 'They'} can view the books.`, () => access.set(r.user_id, 'view'))}>Set View</button>{' '}
                              <button type="button" className="lp-btn lp-btn--small" disabled={r.level === 'edit'} onClick={() => act(`${r.email ?? 'They'} can edit the books.`, () => access.set(r.user_id, 'edit'))}>Set Edit</button>{' '}
                              {r.level != null && (
                                <ConfirmButton
                                  label="Remove"
                                  tone="danger"
                                  sentence={r.is_admin
                                    ? `Remove ${r.email ?? 'this owner'}'s books access row. As an owner they can still view; they lose edit.`
                                    : `Remove ${r.email ?? 'this person'}'s access to the books. They can be added again later.`}
                                  yes="Yes, remove it"
                                  onConfirm={() => act(`Removed ${r.email ?? 'their'} access.`, () => access.set(r.user_id, null))}
                                />
                              )}
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!isAdmin && rows && rows.length > 0 && (
          <p className="lp-field-hint" style={{ marginTop: 10 }}>Only an owner can change who has access.</p>
        )}
      </Section>

      {isAdmin && (
        <Section title="Give access by email">
          <div className="lp-form">
            <div className="lp-form-grid">
              <Field label="Email" hint="They need a Rootegra account already. If not, use Invite."><input className="lp-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" /></Field>
              <Field label="Access">
                <select className="lp-sel" value={level} onChange={(e) => setLevel(e.target.value as AccessLevel)}>
                  <option value="view">View</option>
                  <option value="edit">Edit</option>
                </select>
              </Field>
            </div>
            <div className="lp-row">
              <button type="button" className="lp-btn lp-btn--primary" disabled={giving || !email.trim()} onClick={() => void giveByEmail()}>{giving ? 'Saving…' : 'Give access'}</button>
              {superAdmin && !showInvite && (
                <button type="button" className="lp-btn lp-btn--quiet" onClick={() => { setShowInvite(true); setInviteEmail(email.trim().toLowerCase()); setInviteLevel(level); }}>Invite someone new instead</button>
              )}
            </div>
          </div>
        </Section>
      )}

      {isAdmin && showInvite && (
        <Section title="Invite the accountant">
          {superAdmin ? (
            <div className="lp-form">
              <p className="lp-field-hint" style={{ marginTop: 0 }}>
                They get an email with a sign-in link and land straight in Books with the access you pick. No other part of Backbar opens for them.
              </p>
              <div className="lp-form-grid">
                <Field label="Email"><input className="lp-inp" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="accountant@example.com" /></Field>
                <Field label="Access">
                  <select className="lp-sel" value={inviteLevel} onChange={(e) => setInviteLevel(e.target.value as AccessLevel)}>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                  </select>
                </Field>
              </div>
              <div className="lp-row">
                <ConfirmButton
                  label="Send the invite"
                  tone="primary"
                  disabled={inviting || !inviteEmail.trim()}
                  sentence={`This emails ${inviteEmail.trim() || 'them'} a sign-in link and gives them ${inviteLevel} access to the books.`}
                  yes="Yes, send it"
                  busyLabel="Sending…"
                  onConfirm={invite}
                />
                <button type="button" className="lp-btn lp-btn--quiet" disabled={inviting} onClick={() => setShowInvite(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <Note>Only Laura or Leah can send an invite. Ask one of them to invite {inviteEmail || 'the accountant'}.</Note>
          )}
        </Section>
      )}

      <Section title="Log" right={audit ? <span className="muted">Latest {audit.length}</span> : undefined}>
        <ErrorNote error={auditErr} />
        {audit === null && !auditErr && <Empty>Loading…</Empty>}
        {audit && audit.length === 0 && <Empty>Nothing logged yet.</Empty>}
        {audit && audit.length > 0 && (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Target</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateTime(a.at)}</td>
                    <td>{a.actor_email ?? (a.actor ? `${a.actor.slice(0, 8)}…` : 'System')}</td>
                    <td>{actionWords(a.action)}{ACTION_WORDS[a.action] ? null : <span className="muted"> ({a.action})</span>}</td>
                    <td>{targetWords(a)}</td>
                    <td>
                      <details>
                        <summary className="lp-btn lp-btn--quiet lp-btn--small" style={{ display: 'inline-flex', cursor: 'pointer' }}>Details</summary>
                        <pre style={{ margin: '8px 0 0', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 520 }}>{JSON.stringify({ action: a.action, target_type: a.target_type, target_id: a.target_id, details: a.details }, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}
